export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          <p className="mt-2 text-sm text-gray-600">Last Updated: October 13, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-gray max-w-none">

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Welcome!</h2>
            <p className="text-gray-700 leading-relaxed">
              By using this site, you agree to these terms. This is a free, personal project - written in Plain English to be reasonable while protecting both of us. If you don't agree, please don't use the site.
            </p>
          </section>

          {/* The Service */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What This Service Does</h2>
            <p className="text-gray-700 leading-relaxed">
              This site provides free access to job listings with compensation data.
            </p>
          </section>

          {/* Data Accuracy Disclaimer */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">⚠️ Important: Data Accuracy Disclaimer</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-gray-900 font-semibold mb-2">Job data may be outdated, incomplete, or inaccurate.</p>
              <p className="text-gray-700 leading-relaxed">
                All job listings are automatically collected from third-party sources.
                I have no control over the accuracy of this data. Jobs may be:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                <li>Already filled or closed</li>
                <li>Posted with incorrect information</li>
                <li>Missing important details</li>
                <li>Changed by the employer after being posted</li>
              </ul>
            </div>
            <p className="text-gray-700 leading-relaxed">
              <strong>Always verify job details directly with the employer</strong> before applying.
              I am not responsible for any decisions you make based on data found on this site.
            </p>
          </section>

          {/* No Warranties */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">No Warranties</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This service is provided <strong>"AS-IS"</strong> and <strong>"AS AVAILABLE"</strong>, with no warranties of any kind.
              This means:
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">No Guarantee of Accuracy</p>
                <p className="text-gray-600 text-sm">I don't guarantee that job data is accurate, complete, or up-to-date</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">No Guarantee of Uptime</p>
                <p className="text-gray-600 text-sm">The site may go down, have bugs, or stop working at any time</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">No Guarantee of Results</p>
                <p className="text-gray-600 text-sm">Using this site doesn't guarantee you'll find a job or that employers will respond</p>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To the fullest extent permitted by law, I am not liable for any damages resulting from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Incorrect or outdated job data</li>
              <li>Broken links or expired job listings</li>
              <li>Hiring decisions or employment outcomes</li>
              <li>Third-party platform issues (ATS downtime, Substack, Plain, etc.)</li>
              <li>Lost opportunities or missed applications</li>
              <li>Any other use of this service</li>
            </ul>
            <p className="text-gray-700 mt-4 font-semibold">
              Use this site at your own risk. If something goes wrong, the most I could owe you is nothing -
              because the service is free.
            </p>
          </section>

          {/* Third-Party Platforms */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Platforms</h2>
            <p className="text-gray-700 leading-relaxed">
              When you click "Apply" on a job, you'll be redirected to the employer's application page on their platform. Your application is handled entirely by them - not by me.
            </p>
          </section>

          {/* Acceptable Use */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptable Use Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree NOT to:
            </p>
            <div className="space-y-2">
              <div className="flex items-start">
                <span className="text-red-600 mr-3">✗</span>
                <p className="text-gray-700">Scrape or copy the job database for commercial use</p>
              </div>
              <div className="flex items-start">
                <span className="text-red-600 mr-3">✗</span>
                <p className="text-gray-700">Use automated tools to access or download data</p>
              </div>
              <div className="flex items-start">
                <span className="text-red-600 mr-3">✗</span>
                <p className="text-gray-700">Reverse engineer or attempt to access backend systems</p>
              </div>
              <div className="flex items-start">
                <span className="text-red-600 mr-3">✗</span>
                <p className="text-gray-700">Post spam, abuse the contact form, or engage in illegal activity</p>
              </div>
              <div className="flex items-start">
                <span className="text-red-600 mr-3">✗</span>
                <p className="text-gray-700">Overload the servers or disrupt the service for others</p>
              </div>
            </div>
            <p className="text-gray-700 mt-4">
              If you violate these rules, I may block your access to the site.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              Job data belongs to the companies that posted them. My AI analysis, site design, and code are my intellectual property. You're welcome to use the site for personal job searching, but don't copy the site or use the data commercially without permission.
            </p>
          </section>

          {/* Changes & Termination */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes & Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              I can modify, remove, or discontinue any part of the service at any time. I may also update these terms - check the "Last Updated" date at the top. Continued use means you accept changes. If you violate these terms, I may block your access.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Me</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Questions about these terms? Want to discuss something? I'm happy to help.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-900 mb-2">
                Use the contact form by clicking the message icon in the bottom-right corner of any page.
              </p>
              <p className="text-sm text-gray-600">
                This is a personal project operated by an individual, not a registered business.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}
